import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ListChecks, UserCircle, Warning, CheckCircle, Users, User } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/DataTable';
import { useT } from '@/i18n/LanguageContext';
import { plannerApi } from './plannerApi';
import { fmtDate, PRIORITY_BADGE, TASK_STATUS_DOT, ganttOrder, dispText } from './plannerConstants';

const FILTERS = ['all', 'overdue', 'due_soon'];

export default function MyTasks() {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ employee_linked: true, tasks: [] });
  const [includeDone, setIncludeDone] = useState(false);

  // B4 — ホーム／部署のカードから「すべて見る」で来たときは全員分の一覧を出す。
  const scope = params.get('scope') === 'all' ? 'all' : 'mine';
  const filter = FILTERS.includes(params.get('filter')) ? params.get('filter') : 'all';
  const departmentId = params.get('department_id') || '';

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    plannerApi.myTasks({
      include_done: includeDone,
      scope,
      ...(filter !== 'all' ? { filter } : {}),
      ...(departmentId ? { department_id: departmentId } : {}),
    })
      .then(setData)
      .catch(() => setData({ employee_linked: true, tasks: [] }));
  }, [includeDone, scope, filter, departmentId]);

  // ガントと同じ順序: 優先度（高い順）→ 期限（早い順）
  const tasks = useMemo(
    () => [...(data.tasks || [])].sort(ganttOrder((tk) => tk.due_date || tk.start_date, (tk) => tk.start_date || tk.due_date)),
    [data.tasks]
  );
  const deptLabel = scope === 'all' && departmentId
    ? (tasks.find((tk) => tk.department_name)?.department_name || '') : '';

  const title = scope === 'all'
    ? (filter === 'overdue' ? t('planner.myTasks.allOverdueTitle')
      : filter === 'due_soon' ? t('planner.myTasks.allDueSoonTitle')
      : t('planner.myTasks.allTitle'))
    : t('planner.myTasks.title');

  const chip = (active) => `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
    active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50'}`;

  return (
    <div className="space-y-5" data-testid="planner-my-tasks">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900">{title}</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {scope === 'all' ? t('planner.myTasks.allSubtitle') : t('planner.myTasks.subtitle')}
            {deptLabel && <span className="ml-1 text-zinc-400">· {deptLabel}</span>}
          </p>
        </div>
        <button onClick={() => setIncludeDone((v) => !v)} data-testid="mytasks-toggle-done"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${includeDone ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50'}`}>
          <CheckCircle size={14} weight={includeDone ? 'fill' : 'regular'} /> {t('planner.myTasks.showDone')}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setParam('scope', '')} className={chip(scope === 'mine')} data-testid="mytasks-scope-mine">
          <User size={14} weight={scope === 'mine' ? 'fill' : 'regular'} /> {t('planner.myTasks.scopeMine')}
        </button>
        <button type="button" onClick={() => setParam('scope', 'all')} className={chip(scope === 'all')} data-testid="mytasks-scope-all">
          <Users size={14} weight={scope === 'all' ? 'fill' : 'regular'} /> {t('planner.myTasks.scopeAll')}
        </button>
        <span className="h-5 w-px bg-zinc-200 mx-1" />
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setParam('filter', f === 'all' ? '' : f)}
            className={chip(filter === f)} data-testid={`mytasks-filter-${f}`}>
            {t(`planner.myTasks.filter.${f}`)}
          </button>
        ))}
        {departmentId && (
          <button type="button" onClick={() => setParam('department_id', '')} className={chip(false)} data-testid="mytasks-clear-dept">
            {t('planner.myTasks.clearDept')}
          </button>
        )}
        <span className="ml-auto text-xs text-zinc-400 tabular-nums" data-testid="mytasks-count">
          {t('planner.count', { n: tasks.length })}
        </span>
      </div>

      {data.employee_linked === false ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center" data-testid="mytasks-not-linked">
          <UserCircle size={40} weight="duotone" className="text-amber-500 mx-auto mb-2" />
          <p className="text-sm text-amber-800">{t('planner.myTasks.notLinked')}</p>
        </div>
      ) : (
        <DataTable
          dataTestId="mytasks-table"
          rows={tasks}
          rowKey={(tk) => tk.id}
          rowTestId={(tk) => `mytask-row-${tk.id}`}
          onRowClick={(tk) => navigate(
            `/planner/project/${tk.project_id}?task=${tk.id}&from=${encodeURIComponent(location.pathname + location.search)}`)}
          empty={{ icon: <ListChecks size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('planner.myTasks.empty') }}
          columns={[
            { key: 'title', primary: true, header: t('planner.task.title'), cell: (tk) => <span className="font-medium text-zinc-800"><span data-no-translate>{dispText(tk.title)}</span></span> },
            { key: 'priority', header: t('planner.task.priority'), mobileBadge: true, width: 'w-28', cell: (tk) => <Badge variant="outline" className={`${PRIORITY_BADGE[tk.priority]} text-[11px]`}>{t(`planner.priority.${tk.priority}`)}</Badge> },
            { key: 'status', header: t('planner.task.status'), width: 'w-32', cell: (tk) => <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600"><span className={`w-2 h-2 rounded-full ${TASK_STATUS_DOT[tk.status]}`} />{t(`planner.taskStatus.${tk.status}`)}</span> },
            ...(scope === 'all' ? [{
              key: 'members', header: t('planner.task.members'), width: 'w-40',
              sortValue: (tk) => (tk.member_names || []).join(' '),
              cell: (tk) => <span className="text-xs text-zinc-500">{(tk.member_names || []).join('、') || t('planner.people.none')}</span>,
            }] : []),
            { key: 'project', header: t('planner.myTasks.project'), sortValue: (tk) => tk.project_name || '', cell: (tk) => (
              <span className="text-xs text-zinc-500">
                <span data-no-translate>{dispText(tk.project_name)}</span>
                <span className="text-zinc-300 mx-1">·</span>
                <span className="text-zinc-400">{tk.department_name}</span>
                {tk.project_finished && (
                  <span className="ml-1.5 rounded bg-zinc-100 px-1 py-px text-[10px] text-zinc-500"
                    data-testid={`mytask-finished-${tk.id}`}>{t('planner.project.finishedTag')}</span>
                )}
              </span>
            ) },
            { key: 'due', header: t('planner.task.due'), align: 'right', width: 'w-32', sortValue: (tk) => tk.due_date, cell: (tk) => (
              <span className={tk.overdue ? 'text-rose-600 font-semibold inline-flex items-center gap-1 justify-end tabular-nums text-xs' : 'text-zinc-600 tabular-nums text-xs'}>
                {tk.overdue && <Warning size={12} weight="fill" />}{fmtDate(tk.due_date)}
              </span>
            ) },
          ]}
        />
      )}
    </div>
  );
}
